export function getCsrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}

/**
 * Error envelope rendered by the Rails `ApiErrorHandling` concern:
 * `{ error: <user-facing message>, code: <stable machine code>, details?: {...} }`.
 * Branch on `code`, show `message`.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function toApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
  const body: { error?: string; code?: string; details?: Record<string, unknown> } = await response
    .json()
    .catch(() => ({}));
  return new ApiError(
    body.error ?? fallbackMessage,
    response.status,
    body.code ?? `http_${response.status}`,
    body.details
  );
}

export async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...options.headers,
    },
  });

  if (!response.ok) throw await toApiError(response, "Request failed");

  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function apiUpload<T>(url: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers: { "X-CSRF-Token": getCsrfToken() },
    body: formData,
  });

  if (!response.ok) throw await toApiError(response, "Upload failed");

  return response.json() as Promise<T>;
}
